import { useEffect, useState, type ReactNode } from 'react';
import { Check, Hourglass, ListTodo, Send, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  TASK_AI_BUILDER_NAME,
  type TaskMember,
  type TaskSearchHit,
  type TaskWaiting,
} from '@shared/types/tasks';
import { useFormatTaskRef } from '@/lib/current-project';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { MemberAvatar } from '@/components/team/MemberAvatar';
import { Spinner } from '@/components/ui/spinner';
import { LoadError } from '@/components/LoadError';
import { searchTasks } from '@/lib/api/omni-search';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { cn } from '@/lib/utils';
import { AiBuilderAvatar, memberHandle, memberName } from './task-chips';
import { AiMachineItems, type AiMachineChoice } from './AiMachineItems';
import { aiMachineMatches } from './use-ai-machines';
import { taskPerson } from './task-people';
import { waitingKey } from './task-waiting';
import { useBoardPath } from '@/navigation/project-paths';

/**
 * Waiting on: whose turn a task is. People, other tasks (until they're done), the AI builder and
 * upstream (T-58: the task's source, while questions or suggestions sent there wait for a reply),
 * beside the assignees, who keep the task. Waits never move it between columns.
 */

/** Toggle who it's waiting on: the AI builder (when the project has it), people, or a task found by number or title. */
export function WaitingPicker({
  members,
  aiBuilder,
  aiMachine,
  upstream,
  selected,
  taskId,
  onChange,
  defaultOpen = false,
  onOpenChange,
  children,
}: {
  /** Open as soon as it appears (picked from the properties' + menu). */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  members: TaskMember[];
  aiBuilder: boolean;
  /** SF-T-174: also offer the AI builder on a chosen machine (people who may start runs). */
  aiMachine?: AiMachineChoice;
  /** T-58: the task's source ("Asana"), on a task mirrored from an enabled one: it can be waited on too. */
  upstream?: string;
  selected: TaskWaiting[];
  /** The task itself, which can't wait on itself. */
  taskId: string;
  onChange: (next: TaskWaiting[]) => void;
  children: ReactNode;
}) {
  const formatRef = useFormatTaskRef();
  const [open, setOpenState] = useState(defaultOpen);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
    if (next) aiMachine?.load?.();
  };
  // Opened as it appears (from the + menu): the machines load straight away too.
  useEffect(() => {
    if (defaultOpen) aiMachine?.load?.();
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 250);
  // Results remember the query they answer, so a stale answer never shows.
  const [result, setResult] = useState<{ query: string; hits: TaskSearchHit[]; failed?: boolean } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const searching = query.length >= 1;
  const loading = searching && result?.query !== query;
  const failed = searching && result?.query === query && result.failed;
  const hits = searching && result?.query === query ? result.hits.filter((h) => h.id !== taskId && !h.is_done) : [];

  useEffect(() => {
    if (!searching) return undefined;
    let cancelled = false;
    searchTasks(query, 6, { throwOnError: true }).then(
      (results) => {
        if (!cancelled) setResult({ query, hits: results });
      },
      () => {
        if (!cancelled) setResult({ query, hits: [], failed: true });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [query, searching, attempt]);

  const keys = new Set(selected.map(waitingKey));
  const toggle = (item: TaskWaiting) => {
    const key = waitingKey(item);
    onChange(keys.has(key) ? selected.filter((w) => waitingKey(w) !== key) : [...selected, item]);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search people or tasks…" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{loading ? null : 'No one found.'}</CommandEmpty>
            {aiBuilder &&
            (!search ||
              TASK_AI_BUILDER_NAME.toLowerCase().includes(search.trim().toLowerCase()) ||
              (aiMachine?.machines ?? []).some((m) => aiMachineMatches(m, search))) ? (
              <CommandGroup>
                <CommandItem value="ai" onSelect={() => toggle({ target: 'ai' })}>
                  <AiBuilderAvatar />
                  <span className="flex-1">{TASK_AI_BUILDER_NAME}</span>
                  {keys.has('ai') ? <Check className="size-4" /> : null}
                </CommandItem>
                {/* SF-T-174: on a chosen machine (picking one asks the AI builder too). */}
                {aiMachine ? (
                  <AiMachineItems
                    choice={aiMachine}
                    search={TASK_AI_BUILDER_NAME.toLowerCase().includes(search.trim().toLowerCase()) ? '' : search}
                    onPicked={() => {
                      setOpen(false);
                      setSearch('');
                    }}
                  />
                ) : null}
              </CommandGroup>
            ) : null}
            {upstream && (!search || `${upstream} upstream`.toLowerCase().includes(search.trim().toLowerCase())) ? (
              <CommandGroup>
                <CommandItem value="upstream" onSelect={() => toggle({ target: 'upstream', name: upstream })}>
                  <UpstreamFace />
                  <span className="flex-1">{upstream} (the task’s source)</span>
                  {keys.has('upstream') ? <Check className="size-4" /> : null}
                </CommandItem>
              </CommandGroup>
            ) : null}
            <CommandGroup heading="People">
              {members
                .filter((m) => !search.trim() || `${memberName(m)} ${m.email} ${m.handle ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()))
                .map((m) => (
                  <CommandItem key={m.user_id} value={`user:${m.user_id}`} onSelect={() => toggle({ target: 'user', user_id: m.user_id })}>
                    <MemberAvatar name={memberName(m)} photourl={m.photourl} size="sm" hint={false} />
                    <span className="flex-1 truncate">{memberName(m)}</span>
                    {keys.has(`user:${m.user_id}`) ? <Check className="size-4" /> : null}
                  </CommandItem>
                ))}
            </CommandGroup>
            {searching ? (
              <CommandGroup heading="Tasks">
                {loading ? (
                  <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
                    <Spinner size="xs" />
                    Searching tasks…
                  </div>
                ) : failed ? (
                  <div className="px-2 py-1">
                    <LoadError compact title="Couldn't search tasks" onRetry={() => setAttempt((n) => n + 1)} />
                  </div>
                ) : (
                  hits.map((h) => (
                    <CommandItem
                      key={h.id}
                      value={`task:${h.id}`}
                      onSelect={() => toggle({ target: 'task', task_id: h.id, number: h.number, title: h.title, done: h.is_done })}
                    >
                      <ListTodo className="size-4 text-muted-foreground" />
                      <span className="flex-1 truncate">
                        <span className="mr-1 text-xs tabular-nums text-muted-foreground">{formatRef(h.number)}</span>
                        {h.title}
                      </span>
                      {keys.has(`task:${h.id}`) ? <Check className="size-4" /> : null}
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** One wait as a chip in the task's properties, with an X to stop waiting. */
export function WaitingChip({
  waiting,
  members,
  onRemove,
  onNavigate,
  className,
}: {
  waiting: TaskWaiting;
  members: TaskMember[];
  onRemove?: () => void;
  onNavigate?: () => void;
  className?: string;
}) {
  const formatRef = useFormatTaskRef();
  const boardPath = useBoardPath();
  let face: ReactNode;
  let label: ReactNode;
  let name: string;
  if (waiting.target === 'user') {
    const member = taskPerson(members, waiting.user_id);
    name = memberName(member);
    face = <MemberAvatar name={name} photourl={member?.photourl} size="sm" hint={false} />;
    label = <span className="max-w-[10rem] truncate">{memberHandle(member)}</span>;
  } else if (waiting.target === 'upstream') {
    name = waiting.name;
    face = <UpstreamFace />;
    label = <span>{waiting.name}</span>;
  } else if (waiting.target === 'task') {
    name = `${formatRef(waiting.number)} ${waiting.title}`;
    face = <ListTodo className="mx-1 size-4 text-muted-foreground" aria-hidden />;
    label = (
      <Link to={boardPath(waiting.task_id)} onClick={onNavigate} className={cn('max-w-[12rem] truncate hover:underline', waiting.done && 'line-through')}>
        {formatRef(waiting.number)} {waiting.title}
      </Link>
    );
  } else {
    name = TASK_AI_BUILDER_NAME;
    face = <AiBuilderAvatar />;
    label = <span>{TASK_AI_BUILDER_NAME}</span>;
  }
  return (
    <span data-hint={`Waiting on ${name}`} className={cn('inline-flex h-7 items-center gap-1.5 rounded-full pl-0.5 pr-1 text-sm', className)}>
      {face}
      {label}
      {onRemove ? (
        <button
          type="button"
          aria-label={`Stop waiting on ${name}`}
          className="flex size-5 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={onRemove}
        >
          <X className="size-3" />
        </button>
      ) : null}
    </span>
  );
}

/**
 * The card's "waiting on" faces: an hourglass, then whose turn it is. `yourTurn`: it's waiting on
 * the person looking, which reads "Your turn" instead.
 */
export function WaitingFaces({
  waiting,
  members,
  yourTurn,
  hint,
  aiMachine,
  max = 2,
}: {
  waiting: TaskWaiting[];
  members: TaskMember[];
  /** SF-T-174: the machine chosen for the AI builder's runs, named in the hint ("AI builder on jackalope"). */
  aiMachine?: string;
  /** Shown as Your turn instead, even with nothing in waiting (T-92: a sub-task waits on me). */
  yourTurn?: boolean;
  /** Your turn's hint, when it isn't the task itself waiting on me. */
  hint?: string;
  max?: number;
}) {
  const formatRef = useFormatTaskRef();
  if (yourTurn) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 font-medium text-warning-strong" data-hint={hint ?? 'It’s waiting on you'}>
        <Hourglass className="size-3.5" aria-hidden />
        Your turn
      </span>
    );
  }
  if (!waiting.length) return null;
  const names = waiting.map((w) =>
    w.target === 'user'
      ? memberName(taskPerson(members, w.user_id))
      : w.target === 'task'
        ? formatRef(w.number)
        : w.target === 'upstream'
          ? w.name
          : aiMachine
            ? `${TASK_AI_BUILDER_NAME} on ${aiMachine}`
            : TASK_AI_BUILDER_NAME
  );
  const shown = waiting.slice(0, max);
  return (
    <span className="inline-flex shrink-0 items-center gap-1" data-hint={`Waiting on ${names.join(', ')}`}>
      <Hourglass className="size-3.5" aria-hidden />
      <span className="flex -space-x-1.5">
        {shown.map((w) =>
          w.target === 'user' ? (
            <MemberAvatar
              key={waitingKey(w)}
              name={memberName(taskPerson(members, w.user_id))}
              photourl={taskPerson(members, w.user_id)?.photourl}
              size="xs"
              ring
              hint={false}
            />
          ) : w.target === 'ai' ? (
            <AiBuilderAvatar key="ai" size="xs" ring />
          ) : w.target === 'upstream' ? (
            <UpstreamFace key="upstream" size="xs" ring name={w.name} />
          ) : (
            <span key={waitingKey(w)} className="relative rounded bg-muted px-1 text-3xs font-medium tabular-nums ring-2 ring-background">
              {formatRef(w.number)}
            </span>
          )
        )}
      </span>
      {waiting.length > max ? <span className="text-3xs">+{waiting.length - max}</span> : null}
    </span>
  );
}

/**
 * T-58: the face of an upstream wait (the task's source): a round badge with a send arrow. `name` where
 * no text says it (a card's faces), so it's read out as "Waiting on Asana".
 */
function UpstreamFace({ size = 'sm', ring = false, name }: { size?: 'xs' | 'sm'; ring?: boolean; name?: string }) {
  return (
    <span
      {...(name ? { role: 'img', 'aria-label': `Waiting on ${name}` } : { 'aria-hidden': true })}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full border border-info-border bg-info-soft text-info-strong',
        size === 'xs' ? 'size-5 [&_svg]:size-3' : 'size-6 [&_svg]:size-3.5',
        ring && 'ring-2 ring-background'
      )}
    >
      <Send aria-hidden />
    </span>
  );
}
